import React, {useCallback, useEffect, useRef} from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {DetailsMenu} from "./SpaceDetailsMenu";
import { SpaceDetail } from "interfaces/space";
import { useTranslate } from "@pankod/refine-core";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { spaceDetailConstants } from "constants/spaceDetailConstants";
import { scrollToTop } from "utils/scroll";
import { spaceActions } from "store/space";
import { Focusable } from "components/Focusable";
import SpaceEventTypeForm from "./services/SpaceEventTypeForm";
import SpaceOccupancyForm from "./services/SpaceOccupancyForm";
import SpaceGroupsForm from "./services/SpaceGroupsForm";

interface SpaceDetailsServicesProps {
    spaceDetail: SpaceDetail
    onScrollApplied: (item: DetailsMenu) => void
}

const SpaceDetailsServices: React.FC<SpaceDetailsServicesProps> = (props) => {

    const {spaceDetail, onScrollApplied} = props
    const t = useTranslate()
    const header = t('updateSpace.menuServices')

    const eventTypeRef = useRef<HTMLElement>(null)
    const occupancyRef = useRef<HTMLElement>(null)
    const groupsRef = useRef<HTMLElement>(null)

    const dispatch = useAppDispatch()
    const currentFocusField = useAppSelector(state => state.space.currentFocusId)
    const shouldFocus = useAppSelector(state => state.space.shouldFocus)

    const focusCurrentField = useCallback(() => {
        if (shouldFocus) {
            switch (currentFocusField) {
                case spaceDetailConstants.eventType:
                    scrollToTop(eventTypeRef, spaceDetailConstants.barOffset)
                    dispatch(spaceActions.setShouldFocus(false))
                    onScrollApplied(DetailsMenu.Services)
                    break;
                case spaceDetailConstants.occupancy:
                    scrollToTop(occupancyRef, spaceDetailConstants.barOffset)
                    dispatch(spaceActions.setShouldFocus(false))
                    onScrollApplied(DetailsMenu.Services)
                    break;
                case spaceDetailConstants.groups:
                    scrollToTop(groupsRef, spaceDetailConstants.barOffset)
                    dispatch(spaceActions.setShouldFocus(false))
                    onScrollApplied(DetailsMenu.Services)
                    break;
            }
        }
    }, [currentFocusField, dispatch, onScrollApplied, shouldFocus])

    //Requires empty dep array to only run once
    useEffect(() => {
        setTimeout(focusCurrentField, spaceDetailConstants.scrollTimeout)
    }, [])

    focusCurrentField()

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
    }}>
        <Typography variant="headlineLarge">{header}</Typography>
        <Box sx={{pt: 6, pr: 4}}>
            <Focusable ref={eventTypeRef}>
                <SpaceEventTypeForm spaceDetail={spaceDetail}/>
            </Focusable>
        </Box>
        <Box sx={{pt: 4, pr: 4}}>
            <Focusable ref={occupancyRef}>
                <SpaceOccupancyForm spaceDetail={spaceDetail}/>
            </Focusable>
        </Box>
        <Box sx={{pt: 4, pr: 4}}>
            <Focusable ref={groupsRef}>
                <SpaceGroupsForm 
                    spaceDetail={spaceDetail}
                />
            </Focusable>
        </Box>
    </Box>
}

export default SpaceDetailsServices